import React, { useContext, useEffect, useState } from 'react';
import styled from 'styled-components';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import RingilSidebar from '@components/ringil3/Sidebar/RingilSidebar';
import { ColWithGap } from '@components/styles.tsx';
import SvgWarningExclamation from '@components/ringil3/icons/WarningExclamation.tsx';
import COInlineMessage from '@components/ringil3/COInlineMessage/COInlineMessage.tsx';
import { LabeledChildren } from '@components/ringil3/Containers/LabeledChildren.tsx';
import ForExpedition from '@icons/ForExpedition.tsx';
import Message from '@icons/Message.tsx';
import { ApiContext } from '@api/api.tsx';
import {
  isValidPhoneNumber,
  isValidStringParameter,
} from '@components/ringil3/Features/Expedition/Sidebars/utils/validation.ts';

interface ComponentProps {
  reservationId: string;
  applicationId: string;
  phoneNumber: string;
  location: string;
  since: string;
}

export const useMessengerTimeslotOnTimeCheckSidebar = () => {
    const [visible, setVisible] = useState(false);

    const MessengerTimeslotOnTimeCheckSidebar: React.FC<ComponentProps> = ({
                                                                             reservationId,
                                                                             applicationId,
                                                                             phoneNumber,
                                                                             location,
                                                                             since,
                                                                           }) => {

      const { tr } = useTranslationLgs();
      const [validPhoneNumber, setValidPhoneNumber] = useState(isValidPhoneNumber(phoneNumber));
      const [validLocation, setValidLocation] = useState(isValidStringParameter(location));
      const [loading, setLoading] = useState(false);

      const { requestTimeslotOnTimeCheckMessage } = useContext(ApiContext);

      useEffect(() => {
        setValidPhoneNumber(isValidPhoneNumber(phoneNumber));
        setValidLocation(isValidStringParameter(location));
      }, [phoneNumber, location]);


      const messageText = tr('MessengerRequestTimeslotOnTimeCheck.messageFormat', 'Customer {{location}} requests confirmation if you will arrive on time for reservation {{applicationId}}, which starts at {{from}}.', {
        applicationId: applicationId,
        location: location,
        from: since,
      });

      const sendMessage = () => {
        setLoading(true);
        requestTimeslotOnTimeCheckMessage(
          {
            phoneNumber: phoneNumber,
            reservationId: reservationId,
          },
          () => {
            setLoading(false);
            setVisible(false);
          },
          null,
          { });
      };

      return (
        <>
          <RingilSidebar
            visible={visible}
            onBack={() => setVisible(false)}
            onContinue={() => {
              sendMessage();

            }}
            heading={tr(`MessengerRequestTimeslotOnTimeCheck.heading`, `Request driver arrival`)}
            showSaveButton={validPhoneNumber}
            continueLabel={tr(`MessengerRequestTimeslotOnTimeCheck.continue`, `Send`)}
            continueIcon={<WhiteMessage />}
            loading={loading}
          >

            <ColWithGap>
              {!validPhoneNumber && (
                <div>
                  <COInlineMessage variant={'error'} icon={<SvgWarningExclamation />}>
                    {tr('MessengerRequestTimeslotOnTimeCheck.invalidPhoneNumber', 'Cannot request driver due to missing or invalid phone number')}
                  </COInlineMessage>
                </div>
              )}
              {!validLocation && (
                <COInlineMessage variant={'error'} icon={<SvgWarningExclamation />}>
                  {tr('MessengerRequestTimeslotOnTimeCheck.invalidLocation', 'Cannot request driver due to missing location information')}
                </COInlineMessage>
              )}
            </ColWithGap>
            {validPhoneNumber && validLocation && (
              <ColWithGap>
                <LabeledChildren
                  label={tr(`MessengerRequestTimeslotOnTimeCheck.forExpedition`, `For expedition`)}
                  icon={<ForExpedition />}

                >
                  <StuledApplicationIdDiv>
                    {applicationId}
                  </StuledApplicationIdDiv>
                </LabeledChildren>
                <LabeledChildren
                  label={tr('MessengerRequestTimeslotOnTimeCheck.sendMessageToNumber', 'Send message to number')}
                >
                  <StyledValueDiv>{phoneNumber}</StyledValueDiv>
                </LabeledChildren>
                <LabeledChildren
                  label={tr('MessengerRequestTimeslotOnTimeCheck.messageText', 'Request on time confirmation message')}
                >
                  <StyledValueDiv>{messageText}</StyledValueDiv>
                </LabeledChildren>
              </ColWithGap>
            )}

          </RingilSidebar>
        </>);

    };

    return { MessengerTimeslotOnTimeCheckSidebar: MessengerTimeslotOnTimeCheckSidebar, setVisible };
  }
;

const WhiteMessage = styled(Message)`
  path {
    fill: white;
  }
`;

const StyledValueDiv = styled.div`
  display: flex;
  height: 40px;
  padding: 10px 12px;
  align-items: center;
  gap: 16px;
  align-self: stretch;
  border: 1px solid var(--content-quaternary, #E7EBEE);
  background: var(--base-basetertiary, #F7F7F7);
`;

const StuledApplicationIdDiv = styled.div`
  color: var(--content-primary, #343333);

  /* Basic Bold */
  font-family: "DM Sans", serif;
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: normal;
  letter-spacing: -0.16px;
`;